import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EntityPath, Protocol, url } from '@environments/environment';
import { IAccountStatus, IAuthAccessToken, IPersonByUser, IAuthPayload, IClientData, IClientDevice, IDeviceInfo, ILoginCode, TProfile, IPersonWithIncludesParams } from '@app-auth/data-access/entities/auth.interface';
import { AppSource, EPersonIncludeType } from '../entities/auth.enum';
import { TranslateAdapter } from 'src/shared/helpers/translateAdapter';
import { PersonWithIncludesDictionary } from '../entities/auth.dictionary';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private http = inject(HttpClient);
    private personWithIncludes = new TranslateAdapter(PersonWithIncludesDictionary);

    public login(payload: Omit<IAuthPayload, 'Code'>): Observable<IAuthAccessToken> {
        return this.http.post<any>(url(Protocol.https, EntityPath.user_service, '/Auth/Login'), payload);
    }

    public getAllPersonsByUser(): Observable<IPersonByUser[]> {
        const origins = 'actuar,treino,facilfit';
        return this.http.get<IPersonByUser[]>(
            url(Protocol.https, EntityPath.user_service, '/UserProfile/GetAllPersonsByUser'),
            {
                params: { origins },
                withCredentials: true
            }
        );
    }

    public choosePersonAccount(personId: string): Observable<IAuthAccessToken> {
        return this.http.post<IAuthAccessToken>(
            url(Protocol.https, EntityPath.user_service, '/Auth/ChangePerson'), {},
            {
                params: { personId },
                withCredentials: true
            }
        );
    }

    public refreshToken(TokenId: string): Observable<IAuthAccessToken> {
        return this.http.post<IAuthAccessToken>(url(Protocol.https, EntityPath.user_service, '/Auth/Refresh'),
            {
                TokenId,
                Origin: 'actuar,treino,facilfit',
            },
            {
                withCredentials: true,
            }
        );
    }

    public getClient(id: string): Observable<IClientData> {
        return this.http.get<IClientData>(url(Protocol.https, EntityPath.base, '/Pessoas/ObterPorId'), {
            params: {
                id
            },
            withCredentials: true,
        });
    }

    /**
     * Helper method to fetch person with specific includes
     * @param personId Person ID
     * @param includeTypes Types of data to be included in the response
     * @returns Observable with person data and requested includes
     */
    public getPersonWithIncludes(
        personId: string,
        includeTypes: EPersonIncludeType[] = [],
    ): Observable<IClientData> {
        return this.getPersonWithIncludesPost({
        personId,
        includes: includeTypes,
        }).pipe(map((e) => this.personWithIncludes.translate(e)));
    }

    public getPersonWithIncludesPost(params: IPersonWithIncludesParams): Observable<TProfile> {
        return this.http.post<TProfile>
        (url(Protocol.https, EntityPath.person_service, '/PersonWithIncludes/GetPersonWithIncludes'),
        params,
        { withCredentials: true }
        );
    }

    public checkAccountStatus(email: string): Observable<IAccountStatus> {
        return this.http.get<IAccountStatus>(url(Protocol.https, EntityPath.user_service, '/Auth/CheckStatus'), {
            params: {
                email
            }
        });
    }

    public sendEmailResetPassword(payload: Pick<IAuthPayload, 'Email'>): Observable<boolean> {
        return this.http.post<boolean>(url(Protocol.https, EntityPath.user_service, '/Auth/SendEmailResetPassword'),
            {
                ...payload,
                AppSource: AppSource.Treino
            }
        );
    }

    public checkResetPasswordCode(payload: Omit<IAuthPayload, 'AppSource' | 'Password'>): Observable<boolean> {
        return this.http.post<boolean>(url(Protocol.https, EntityPath.user_service, '/Auth/CheckResetPasswordCode'),
            payload
        );
    }

    public resetPassword(payload: Omit<IAuthPayload, 'AppSource'>): Observable<boolean> {
        return this.http.post<boolean>(url(Protocol.https, EntityPath.user_service, '/Auth/ResetPassword'),
            payload
        );
    }

    public confirmEmailAccountAndCreatePassword(payload: Omit<IAuthPayload, 'AppSource'>): Observable<boolean> {
        return this.http.post<boolean>(url(Protocol.https, EntityPath.user_service, '/Auth/ConfirmEmailAccountAndCreatePassword'),
            payload
        );
    }

    public refreshClientDevice(device: IClientDevice): Observable<IDeviceInfo> {
        return this.http.post<IDeviceInfo>(url(Protocol.https, EntityPath.client_device_service, '/ClientDevice'),
            {
                ...device
            },
            {
                withCredentials: true,
            }
        );
    }

    public removeClientDevice(deviceId: string): Observable<IDeviceInfo> {
        return this.http.delete<IDeviceInfo>(url(Protocol.https, EntityPath.client_device_service, '/ClientDevice?id=' + deviceId),
            {
                withCredentials: true,
            }
        );
    }

    public generateLoginCode(): Observable<ILoginCode> {
        return this.http.post<ILoginCode>(url(Protocol.https, EntityPath.user_service, '/Auth/GenerateLoginCode'), {}, {
          withCredentials: true
        });
    }

    public loginByCodeTemp(payload : {sid: string, uid: string}): Observable<IAuthAccessToken> {
        return this.http.post<IAuthAccessToken>(url(Protocol.https, EntityPath.user_service, '/Auth/LoginByCodeTemp'), payload, {
            withCredentials: false
        });
    }

    public loginByEasyCode(simpleCode: string): Observable<IAuthAccessToken> {
        return this.http.post<IAuthAccessToken>(
            url(Protocol.https, EntityPath.user_service, '/Auth/SimpleLogin?simpleCode=' + simpleCode),
            null,
            { withCredentials: false  }
        );
    }
}
